import { differenceInDays } from "date-fns";
import { de as deLocale, enUS } from "date-fns/locale";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDateHourly,
  formatDateYYYY,
  formatDateDDMMYY,
  formatDateDDMMM,
  formatDateMMMYY,
} from "./dateFormat";
import type { Locale } from "date-fns";

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_MS = DAY_MS * 30;
const YEAR_MS = DAY_MS * 365;

export const getDateFormatterForXAxis = (
  startDate: Date,
  endDate: Date,
  value: string | number,
  locale: Locale = enUS,
  tickInterval?: number
) => {
  const totalDays = Math.abs(differenceInDays(startDate, endDate));

  if (tickInterval != null && tickInterval > 0) {
    if (tickInterval < DAY_MS) return formatDateHourly(value);
    if (tickInterval < MONTH_MS) {
      return totalDays > 365
        ? formatDateDDMMYY(value)
        : formatDateDDMMM(value, locale);
    }
    if (tickInterval < YEAR_MS) return formatDateMMMYY(value, locale);
    return formatDateYYYY(value);
  }

  if (totalDays < 1) return formatDateHourly(value);
  if (totalDays > 365 * 7) return formatDateYYYY(value);
  if (totalDays < 182) return formatDateDDMMM(value, locale);
  return formatDateMMMYY(value, locale);
};

export const useDateFormatterForXAxis = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language === "de" ? deLocale : enUS;

  return useCallback(
    (
      startDate: Date,
      endDate: Date,
      value: string | number,
      tickInterval?: number
    ) =>
      getDateFormatterForXAxis(startDate, endDate, value, locale, tickInterval),
    [locale]
  );
};
